import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import Layout from '../../components/Layout/Layout';
import { getApiErrorMessage } from '../../utils/httpError';
import PurchasePricing from './components/PurchasePricing';
import PurchaseCheckout from './components/PurchaseCheckout';
import { createPayPalOrder } from './purchaseApi';
import { readPurchaseCheckoutSession, savePurchaseCheckoutSession } from './purchaseHelpers';
import { getPurchasePlanById } from './purchasePlans';
import usePurchasePlans from './usePurchasePlans';
import './Purchase.scss';

function Purchase({ history, location, purchaserEmail }) {
  const { plans, isLoading, errorMessage: plansError, reload } = usePurchasePlans();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  const selectedPlan = useMemo(() => {
    const planId = new URLSearchParams(location.search).get('plan');
    return planId ? getPurchasePlanById(plans, planId) : null;
  }, [location.search, plans]);

  // Details from an interrupted checkout (e.g. cancelled on PayPal) so a retry is prefilled
  const savedSession = useMemo(() => readPurchaseCheckoutSession(), []);
  const sessionMatchesPlan = Boolean(selectedPlan && savedSession.planId === selectedPlan.id);

  // Pricing and checkout share the same pathname, so the layout-level page
  // transition never fires — handle scroll and stale errors here instead.
  useEffect(() => {
    setCheckoutError('');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [selectedPlan]);

  const goToPlans = useCallback(() => {
    history.push('/purchase');
  }, [history]);

  const handleSelectPlan = useCallback(
    (plan) => {
      history.push(`/purchase?plan=${plan.id}`);
    },
    [history],
  );

  const handleCheckout = useCallback(
    async ({ email, quantity }) => {
      if (!selectedPlan) return;

      setIsSubmitting(true);
      setCheckoutError('');

      try {
        savePurchaseCheckoutSession({
          planId: selectedPlan.id,
          email,
          quantity,
          plan: selectedPlan,
        });

        const response = await createPayPalOrder({
          orderType: selectedPlan.orderType,
          quantity,
          email,
        });

        const paymentUrl = response.data?.paymentUrl;
        if (!paymentUrl) {
          throw new Error('PayPal checkout URL was not returned.');
        }

        window.location.assign(paymentUrl);
      } catch (error) {
        setCheckoutError(getApiErrorMessage(error, 'Unable to start PayPal checkout. Please try again.'));
        setIsSubmitting(false);
      }
    },
    [selectedPlan],
  );

  return (
    <Layout>
      <div className="purchase">
        {isLoading ? (
          <div className="purchase-plans-status" data-testid="purchase-plans-loading">
            <Spin size="large" />
            <p className="purchase-callback-title">Loading plans…</p>
          </div>
        ) : plansError && plans.length === 0 ? (
          <div className="purchase-plans-status">
            <Alert type="error" showIcon message={plansError} />
            <Button type="primary" onClick={reload}>
              Try again
            </Button>
          </div>
        ) : selectedPlan ? (
          <div className="purchase-view" key={`checkout-${selectedPlan.id}`}>
            <PurchaseCheckout
              plan={selectedPlan}
              defaultEmail={(sessionMatchesPlan && savedSession.email) || purchaserEmail}
              defaultQuantity={sessionMatchesPlan ? savedSession.quantity : null}
              onBack={goToPlans}
              onSubmit={handleCheckout}
              isSubmitting={isSubmitting}
              errorMessage={checkoutError}
            />
          </div>
        ) : (
          <div className="purchase-view" key="pricing">
            <PurchasePricing plans={plans} onSelectPlan={handleSelectPlan} />
          </div>
        )}
      </div>
    </Layout>
  );
}

const mapStateToProps = (state) => ({
  purchaserEmail: state.userReducer?.userInfo?.email || '',
});

export default compose(withRouter, connect(mapStateToProps))(Purchase);
